import axios, { type AxiosRequestConfig } from 'axios'
import axiosInstance from '../utils/axiosInstance'
import type {
  MsTokenResponse,
  MsAuthErrorBody,
  AuthFeatureResponse,
  CsrfResponse,
  SessionTokenResponse,
  SessionStatusResponse,
} from '../types/auth.types'

/** Derive an absolute epoch-ms expiry from the backend response. */
export function resolveTokenExpiresAtMs(res: MsTokenResponse): number | null {
  if (typeof res.expires_in === 'number') {
    return Date.now() + res.expires_in * 1000
  }
  return null
}

// In local dev, use a path-relative URL so the Vite dev-server proxy (see
// vite.config.ts) intercepts the request on the same origin — required so
// SameSite=Lax session cookies are sent back (they're blocked on cross-origin
// fetch even with credentials: 'include'). localhost can't share a cookie
// domain with a remote backend, so the proxy is the only way to make this
// same-origin locally.
//
// In a real deployment there is no dev server to proxy through, so hit the
// backend directly. This only works if frontend and backend share a common
// parent domain (e.g. qa-agent.app-np.neovance.com / qaapi.app-np.neovance.com
// both under app-np.neovance.com) AND the backend scopes its auth cookies to
// that parent domain (Domain=.app-np.neovance.com) — same-site, so
// SameSite=Lax still allows it despite being cross-origin. If auth still
// fails after this in a deployed environment, check that cookie Domain
// attribute with the backend team; a host-only cookie will never work here.
//
// The iam service is mounted at the `/api/iam` gateway prefix. The v1 auth
// lifecycle routes live under `/public/api/v1/auth` (backend: AuthV1Controller):
//   /api/iam/public/api/v1/auth/{login, callback, csrf, session-token, refresh, logout, session}
const AUTH_BASE_PATH = '/api/iam/public/api/v1/auth'

// `/feature` is NOT part of the v1 auth API. It is still served by the legacy
// OAuthController, whose class mapping is `/public/auth` — so it resolves to
// /api/iam/public/auth/feature. AuthV1Controller has no `/feature` mapping at
// all, so folding this into AUTH_BASE_PATH yields a 404. Keep the two apart.
const LEGACY_AUTH_BASE_PATH = '/api/iam/public/auth'

// Hit the backend directly (NOT the Vite proxy). The httpOnly session cookies
// are host-only on the API host (qaapi); a proxied call goes to the LOCAL
// origin, which has no such cookie. Going direct works because the local dev
// host is a same-site HTTPS subdomain of app-np.neovance.com (see
// docs/LOCAL_QA_SETUP.md), so SameSite=Lax still sends the cookie.
const apiOrigin = () =>
  window._env_.VITE_API_URL || import.meta.env['VITE_API_URL']

const authBase = () => `${apiOrigin()}${AUTH_BASE_PATH}`
const legacyAuthBase = () => `${apiOrigin()}${LEGACY_AUTH_BASE_PATH}`

export class MsAuthApiError extends Error {
  readonly status: number
  readonly body: MsAuthErrorBody | null

  constructor(path: string, status: number, body: MsAuthErrorBody | null) {
    super(`Auth ${path} failed: ${status}`)
    this.name = 'MsAuthApiError'
    this.status = status
    this.body = body
  }
}

type AuthAxiosRequestConfig = AxiosRequestConfig & {
  skipAuthorization?: boolean
}

/**
 * Uses the SAME shared `axiosInstance` as the rest of the app — matching the
 * existing pattern for other no-tenant "public" endpoints (see
 * `getLoggingActions` calling `public/logging` in `apis/user.ts`), rather than
 * a separate axios instance.
 *
 * IMPORTANT: `agent.store.ts`'s response interceptor explicitly excludes the
 * `/public/api/v1/auth/**` paths from its MS_BACKEND refresh-and-retry branch.
 * Without that exclusion, a 401 from *this* call (e.g. an expired refresh
 * session — an expected, normal outcome for `/refresh`) would re-enter the same
 * interceptor and try to refresh again from inside the refresh call already in
 * flight — a deadlock, not a retry. If these auth routes ever move, update that
 * exclusion (and `NO_TENANT_HEADER_API`) too.
 */
async function authRequest<T>(
  path: string,
  opts: {
    method?: 'GET' | 'POST'
    /** Whether to send the httpOnly session cookies with this call. Default true. */
    withCredentials?: boolean
    /** Prevent portal request interceptors from attaching the Redux bearer token. */
    skipAuthorization?: boolean
    extraHeaders?: Record<string, string>
    /** Origin + base path to prefix `path` with. Defaults to the v1 auth base. */
    base?: string
  } = {}
): Promise<T> {
  const {
    method = 'GET',
    withCredentials = true,
    skipAuthorization = false,
    extraHeaders,
    base,
  } = opts
  try {
    const requestConfig: AuthAxiosRequestConfig = {
      url: `${base ?? authBase()}${path}`,
      method,
      withCredentials,
      skipAuthorization,
      headers: {
        Accept: 'application/json',
        ...extraHeaders,
      },
    }
    const response = await axiosInstance.request<T>(requestConfig)
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const body = (err.response?.data ?? null) as MsAuthErrorBody | null
      throw new MsAuthApiError(path, err.response?.status ?? 0, body)
    }
    throw err
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Navigate the browser to the backend login endpoint.
 * The backend (302) redirects to Microsoft Entra; after the user authenticates,
 * Microsoft redirects to the backend callback which sets the HttpOnly session
 * cookie and forwards the browser back to `redirectUri` (default: app origin).
 *
 * Note: custom request headers cannot be set on browser navigation.
 * Pass `returnPath` as a RELATIVE UI path (e.g. "/cases/123?tab=x", or "/" at the
 * app root — which encodes to `%2F`). The backend VALIDATES this and rejects an
 * absolute URL with `AUTH_REQUEST_INVALID`; it appends the relative path to its
 * own allowlisted UI origin to send the user back after the OAuth round trip.
 * So callers pass path + query + hash only — never `window.location.href`.
 *
 * `origin` is sent as a QUERY PARAM because this endpoint is entered by a
 * top-level GET navigation, and browsers attach the `Origin` HEADER only to
 * CORS-mode requests and to navigations using an unsafe method (POST/PUT/...).
 * A GET navigation therefore carries no `Origin` header, and no client API can
 * add one — it is a forbidden header name. The param is the only way to tell
 * the backend which UI origin started the flow.
 *
 * SECURITY: unlike the real `Origin` header, this value is caller-controlled
 * and is NOT proof of provenance. The backend MUST match it against its own
 * allowlist and fall back to a configured default on mismatch — never echo it
 * back into a redirect unvalidated, or it becomes an open redirect.
 */
export function initiateLogin(returnPath?: string): void {
  const params = new URLSearchParams({ source: 'agent' })
  params.set('origin', window.location.origin)
  if (returnPath) params.set('returnPath', returnPath)
  window.location.href = `${authBase()}/login?${params.toString()}`
}

// NOTE: the legacy `GET /token` (pickup-cookie swap), `GET /refresh`
// (Bearer-proven), and `GET /logout/v2` (credential-less) calls have been
// replaced by the hardened flow below (`fetchSessionToken`, `refreshSessionToken`,
// `logoutSession`) — cookie + CSRF + Origin, no Bearer.

/**
 * GET /auth/feature — returns an array of feature flags (not login-specific;
 * more flags unrelated to auth may be added here in the future). Called once
 * on app boot to look up the "login" flag and determine the auth provider.
 * Public endpoint: no token or cookie required.
 */
export const fetchAuthFeature = (): Promise<AuthFeatureResponse | string> =>
  authRequest<AuthFeatureResponse | string>('/feature', {
    withCredentials: false,
    skipAuthorization: true,
    // Legacy base — see LEGACY_AUTH_BASE_PATH. Not a v1 route.
    base: legacyAuthBase(),
  })

// ─── Hardened flow (replaces the pickup-cookie / bearer-refresh calls above) ────
//
// Proof of identity is now the HttpOnly session cookie + a per-session CSRF token
// (echoed in `X-CSRF-TOKEN`) + the exact Origin — no Bearer header. `withCredentials`
// stays true (default) so the session cookie rides along; `skipAuthorization` keeps
// the stale Redux bearer off these calls. The custom CSRF header makes every one of
// these a CORS preflighted request, so the edge must answer preflight with the exact
// origin + `Access-Control-Allow-Credentials: true` (infra fix, tracked separately).

/** Header the CSRF token is echoed in. Must match `CsrfResponse.headerName`. */
export const CSRF_HEADER = 'X-CSRF-TOKEN'

/**
 * GET /public/api/v1/auth/csrf — fetch the per-session CSRF token.
 * Per route-security §3: proof = valid session cookie; CSRF = No; no Bearer.
 * (withCredentials defaults true → cookie is sent; skipAuthorization strips the
 * bearer.) Caller holds the token in memory, re-fetching once on `AUTH_CSRF_INVALID`.
 */
export const fetchCsrf = (): Promise<CsrfResponse> =>
  authRequest<CsrfResponse>('/csrf', { skipAuthorization: true })

/**
 * POST /public/api/v1/auth/session-token — exchange the session cookie (+ CSRF)
 * for the current access token, refreshing server-side if close to expiry.
 * Per §3: proof = session cookie + CSRF + Origin; CSRF = Yes; no Bearer. The
 * returned token is held in memory only.
 */
export const fetchSessionToken = (
  csrfToken: string
): Promise<SessionTokenResponse> =>
  authRequest<SessionTokenResponse>('/session-token', {
    method: 'POST',
    skipAuthorization: true,
    extraHeaders: { [CSRF_HEADER]: csrfToken },
    withCredentials: true, // send the session cookie
  })

/**
 * POST /public/api/v1/auth/refresh — obtain a fresh access token.
 * Per §3: proof = session cookie + CSRF + Origin + stored refresh token; CSRF =
 * Yes; no Bearer. Both access and refresh rotate server-side. Returns 401
 * (`AUTH_SESSION_EXPIRED`) when the session/refresh is no longer usable.
 */
export const refreshSessionToken = (
  csrfToken: string
): Promise<SessionTokenResponse> =>
  authRequest<SessionTokenResponse>('/refresh', {
    method: 'POST',
    skipAuthorization: true,
    extraHeaders: { [CSRF_HEADER]: csrfToken },
  })

/**
 * POST /public/api/v1/auth/logout — revoke the backend session and clear cookies.
 * Per §3: proof = session cookie + CSRF + Origin; CSRF = Yes; no Bearer; result
 * = 204 No Content (session revoked).
 */
export const logoutSession = (csrfToken: string): Promise<void> =>
  authRequest<void>('/logout', {
    method: 'POST',
    skipAuthorization: true,
    extraHeaders: { [CSRF_HEADER]: csrfToken },
  })

/**
 * GET /public/api/v1/auth/session — read-only "am I still signed in?" check.
 * Per §3: proof = session cookie; read-only (no CSRF); never returns a token.
 * Optional startup aid; the main bootstrap uses `fetchSessionToken`.
 */
export const fetchSessionStatus = (): Promise<SessionStatusResponse> =>
  authRequest<SessionStatusResponse>('/session', {
    skipAuthorization: true,
  })

// ─── In-memory CSRF token holder ────────────────────────────────────────────
//
// The CSRF token is per-session and deliberately kept in a module variable —
// never persisted (no localStorage/sessionStorage), so it shares the access
// token's memory-only lifetime and is gone on reload (re-fetched by the boot
// bootstrap). All state-changing auth calls go through `ensureCsrfToken()`.

let csrfTokenInMemory: string | null = null

/** The current in-memory CSRF token, or null if not yet fetched. */
export const getCsrfToken = (): string | null => csrfTokenInMemory

/**
 * Return a CSRF token, fetching one if absent. Pass `force` to discard the
 * cached token and fetch a fresh one — used exactly once when a call comes back
 * `AUTH_CSRF_INVALID` (the token rotated), before giving up.
 */
export const ensureCsrfToken = async (force = false): Promise<string> => {
  if (!force && csrfTokenInMemory) return csrfTokenInMemory
  const res = await fetchCsrf()
  csrfTokenInMemory = res.token
  return csrfTokenInMemory
}

/** Drop the in-memory CSRF token (on logout / session clear). */
export const clearCsrfToken = (): void => {
  csrfTokenInMemory = null
}

// ─── Error / response helpers (tolerant of legacy + hardened shapes) ────────

/**
 * Pull the AUTH_* code out of an auth error, tolerant of both the hardened
 * envelope (`{ error: { code } }`) and the legacy flat shape (`{ code }` /
 * `{ error: "..." }`).
 */
export const extractAuthCode = (err: unknown): string | null => {
  if (!(err instanceof MsAuthApiError) || !err.body) return null
  const body = err.body as {
    code?: string
    error?: string | { code?: string }
  }
  if (body.error && typeof body.error === 'object')
    return body.error.code ?? null
  if (typeof body.code === 'string') return body.code
  if (typeof body.error === 'string') return body.error
  return null
}

/** True when an error is a rotated-CSRF rejection (403 AUTH_CSRF_INVALID). */
export const isCsrfInvalid = (err: unknown): boolean =>
  err instanceof MsAuthApiError &&
  err.status === 403 &&
  extractAuthCode(err) === 'AUTH_CSRF_INVALID'

/**
 * Run a CSRF-consuming call: fetch/reuse the in-memory CSRF token, and if the
 * call returns AUTH_CSRF_INVALID (the token rotated), refetch once and retry.
 */
export const withCsrf = async <T>(
  fn: (csrfToken: string) => Promise<T>
): Promise<T> => {
  const csrf = await ensureCsrfToken()
  try {
    return await fn(csrf)
  } catch (err) {
    if (isCsrfInvalid(err)) {
      const fresh = await ensureCsrfToken(true)
      return await fn(fresh)
    }
    throw err
  }
}

/**
 * Normalize a session-token / refresh response — tolerant of the hardened
 * camelCase shape and the legacy snake_case shape, so the FE parses the token
 * whichever the backend actually returns.
 */
export const normalizeSessionToken = (
  res: SessionTokenResponse & Partial<MsTokenResponse>
): { accessToken: string; expiresIn: number | null } => ({
  accessToken: res.accessToken ?? res.access_token ?? '',
  expiresIn: res.expiresIn ?? res.expires_in ?? null,
})

/** Convert an `expiresIn` (seconds) to an absolute epoch-ms expiry. */
export const expiresInToMs = (expiresIn: number | null): number | null =>
  typeof expiresIn === 'number' ? Date.now() + expiresIn * 1000 : null
